import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import type { Lane } from '@/lib/protocol-lanes';
import type { KeyboardEvent } from 'react';

/**
 * One corridor of the frise, as SVG (refonte modulaire §7.1; maquette `.jmp`).
 *
 * Outgoing in `bleu-nuit`, solid; incoming in `gray-500`, dashed - the legend
 * under the banner says which is which, and the difference is a shape as much
 * as a colour so it survives a screenshot in grey.
 *
 * ## Three paths for one line, and each earns its place
 *
 * The first is TRANSPARENT and 14 px wide: a 1.5 px line is a target nobody can
 * hit with a mouse, and widening the visible stroke instead would draw a ribbon.
 * The second is the line itself. The third is the arrowhead, a filled triangle
 * closed on the edge the jump lands on - an arrow tells the direction of a
 * corridor that otherwise reads both ways.
 *
 * ## The colours are classes and never attributes
 *
 * `stroke-bleu-nuit`, `fill-gray-500`: `design-system-adherence.test.ts` refuses
 * a literal hex anywhere in `src/`, SVG included, and it is right to - the
 * mockup spelled the noyau out as a stroke attribute six times, and that is six
 * values nobody can restyle.
 *
 * ## The label wears a halo of the banner's own ground
 *
 * `paint-order: stroke` with a 5 px `gray-50` stroke under the ink: the labels
 * sit on the corridors they name, and without the halo a lane crosses its own
 * text. It is drawn UNDER the glyphs, so the ink keeps its full contrast.
 */

export interface JumpLaneProps {
  readonly lane: Lane;
  /** Below the cards, dashed: a jump that ARRIVES at the selected step. */
  readonly incoming: boolean;
  /** « étiquettes » is on (§7.1) - the corridor is drawn either way. */
  readonly showLabel: boolean;
  /** « si premier facteur + n », or « depuis 2 · ... » for an arrival. */
  readonly label: string;
  /** What the group announces: a corridor is a button, so it has to say where it goes. */
  readonly name: string;
  /** The click, when « le clic sur un saut ouvre l'étape visée » is on. */
  readonly onOpen: () => void;
  readonly onPoint: () => void;
  readonly onLeave: () => void;
}

export function JumpLane({
  lane,
  incoming,
  showLabel,
  label,
  name,
  onOpen,
  onPoint,
  onLeave,
}: JumpLaneProps) {
  function onKeyDown(event: KeyboardEvent<SVGGElement>): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onOpen();
  }

  return (
    <g
      data-testid={PROTOCOL_TEST_IDS.jump}
      data-from={lane.jump.from}
      data-to={lane.jump.to}
      // biome-ignore lint/a11y/useSemanticElements: SVG has no <button>, and a corridor is drawn in SVG.
      role="button"
      tabIndex={0}
      aria-label={name}
      className="pointer-events-auto cursor-pointer"
      onClick={onOpen}
      onKeyDown={onKeyDown}
      onMouseEnter={onPoint}
      onMouseLeave={onLeave}
      onFocus={onPoint}
      onBlur={onLeave}
    >
      <path d={lane.d} strokeWidth={14} className="fill-none stroke-transparent" />
      {incoming ? (
        <path
          d={lane.d}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          className="fill-none stroke-gray-500"
        />
      ) : (
        <path d={lane.d} strokeWidth={1.5} className="fill-none stroke-bleu-nuit" />
      )}
      <path d={lane.tip} className={incoming ? 'fill-gray-500' : 'fill-bleu-nuit'} />
      {showLabel ? (
        <text
          x={lane.label.x}
          y={lane.label.y}
          textAnchor={lane.label.anchor}
          strokeWidth={5}
          strokeLinejoin="round"
          className={`stroke-gray-50 text-micro [paint-order:stroke] ${
            incoming ? 'fill-gray-700' : 'fill-bleu-nuit-deep font-semibold'
          }`}
        >
          {label}
        </text>
      ) : null}
    </g>
  );
}
